/**
 * Patch 35.3c — Schedule Drawer.
 *
 * Founder-facing scheduling flow. Answers three questions only:
 *   Where? · When? · Anything special?
 *
 * Reads workspace timezone from the workspace record, stores canonical
 * UTC in the backend, and displays founder-local times. Never calls a
 * provider — this drawer creates a ``scheduled_assets`` row only; the
 * publishing worker is a later stage.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import {
  scheduleAsset, getCalendar, type ChannelKey,
} from "@/entities/mission-control";

/** Patch 35.3c · destination catalogue mirrors backend CHANNEL_META. */
const DESTINATIONS: Array<{ key: ChannelKey; label: string; hint?: string }> = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "x", label: "X" },
  { key: "threads", label: "Threads" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
  { key: "pinterest", label: "Pinterest" },
  { key: "blog", label: "Blog" },
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS" },
];

interface Props {
  workspaceId: string;
  workspaceTimezone: string;
  assetId: string;
  assetPreview: string;
  onOpenChange: (open: boolean) => void;
}

function fmtLocalPreview(dateISO: string, timeStr: string, tz: string): string {
  if (!dateISO || !timeStr) return "";
  try {
    // Interpret input as local time in workspace timezone.
    const [year, month, day] = dateISO.split("-").map((n) => parseInt(n, 10));
    const [hour, minute] = timeStr.split(":").map((n) => parseInt(n, 10));
    if (!year || !month || !day || Number.isNaN(hour)) return "";
    const d = new Date(Date.UTC(year, month - 1, day, hour, minute));
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      dateStyle: "medium",
      timeStyle: "short",
      timeZoneName: "short",
    }).format(d);
  } catch {
    return "";
  }
}

function toUtcISO(dateISO: string, timeStr: string, tz: string): string | null {
  if (!dateISO || !timeStr) return null;
  try {
    const [year, month, day] = dateISO.split("-").map((n) => parseInt(n, 10));
    const [hour, minute] = timeStr.split(":").map((n) => parseInt(n, 10));
    if (!year || !month || !day || Number.isNaN(hour)) return null;
    // Compute the UTC epoch corresponding to the given wall-clock time
    // in the workspace's IANA timezone. Uses the intl offset trick.
    const guess = Date.UTC(year, month - 1, day, hour, minute);
    // Difference between UTC-interpreted wall-clock and the tz-actual
    // wall-clock at that instant tells us the tz offset.
    const asTz = new Date(new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).format(new Date(guess)));
    const offsetMs = asTz.getTime() - guess;
    return new Date(guess - offsetMs).toISOString();
  } catch {
    return null;
  }
}

function todayIso(tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const g = (t: string) => parts.find((p) => p.type === t)?.value || "";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

export function ScheduleDrawer({
  workspaceId, workspaceTimezone, assetId, assetPreview, onOpenChange,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [channels, setChannels] = useState<ChannelKey[]>([]);
  const tz = workspaceTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [dateISO, setDateISO] = useState<string>(() => {
    // Default to tomorrow in the workspace tz.
    const t = todayIso(tz).split("-").map((n) => parseInt(n, 10));
    const d = new Date(Date.UTC(t[0], t[1] - 1, t[2]));
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [timeStr, setTimeStr] = useState<string>("10:00");
  const [notes, setNotes] = useState<string>("");
  const [confirming, setConfirming] = useState(false);

  const utcISO = useMemo(
    () => toUtcISO(dateISO, timeStr, tz),
    [dateISO, timeStr, tz],
  );
  const localPreview = useMemo(
    () => fmtLocalPreview(dateISO, timeStr, tz),
    [dateISO, timeStr, tz],
  );
  const inPast = useMemo(() => {
    if (!utcISO) return false;
    return new Date(utcISO).getTime() <= Date.now();
  }, [utcISO]);

  const canSubmit = channels.length > 0 && !!utcISO && !inPast;

  const submit = useMutation({
    mutationFn: () =>
      scheduleAsset(workspaceId, {
        asset_id: assetId,
        channels,
        scheduled_at: utcISO!,
      }),
    onSuccess: (events) => {
      toast.success(
        events.length === 1
          ? "Scheduled 1 publication"
          : `Scheduled ${events.length} publications`,
      );
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
      void qc.invalidateQueries({ queryKey: ["pack-summary"] });
      void qc.invalidateQueries({ queryKey: ["asset-lifecycle"] });
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      onOpenChange(false);
    },
    onError: (err: Error & { detail?: { code?: string; message?: string } }) => {
      const detail = (err as unknown as { detail?: { code?: string; message?: string } }).detail;
      const msg = detail?.message || err.message || "Couldn't schedule right now.";
      toast.error(msg);
    },
  });

  const toggleChannel = (k: ChannelKey) =>
    setChannels((prev) => (prev.includes(k) ? prev.filter((c) => c !== k) : [...prev, k]));

  return (
    <Dialog open onOpenChange={(v) => !submit.isPending && onOpenChange(v)}>
      <DialogContent data-testid="schedule-drawer" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule this asset</DialogTitle>
          <DialogDescription>
            Nothing will publish now. Orkexra will safely hold this until its scheduled time.
          </DialogDescription>
        </DialogHeader>

        {!confirming && (
          <div className="space-y-4">
            <div
              data-testid="schedule-asset-preview"
              className="rounded-md border border-border/60 bg-secondary/25 p-2 text-xs"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Asset
              </div>
              <div className="font-medium">{assetPreview}</div>
            </div>

            {/* Destinations */}
            <div>
              <div className="mb-1.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                Where?
              </div>
              <div
                data-testid="schedule-destinations"
                className="flex flex-wrap gap-1.5"
              >
                {DESTINATIONS.map((d) => {
                  const on = channels.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      data-testid={`schedule-destination-${d.key}`}
                      data-selected={on ? "true" : "false"}
                      onClick={() => toggleChannel(d.key)}
                      className={
                        "rounded-full border px-2.5 py-1 text-xs transition " +
                        (on
                          ? "border-primary bg-primary/10 font-medium"
                          : "border-border/60 hover:border-primary/50")
                      }
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date + time */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label
                  htmlFor="schedule-date"
                  className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-muted-foreground"
                >
                  Date
                </label>
                <Input
                  id="schedule-date"
                  data-testid="schedule-date"
                  type="date"
                  value={dateISO}
                  min={todayIso(tz)}
                  onChange={(e) => setDateISO(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="schedule-time"
                  className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-muted-foreground"
                >
                  Time
                </label>
                <Input
                  id="schedule-time"
                  data-testid="schedule-time"
                  type="time"
                  step={900}
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                />
              </div>
            </div>

            <div
              data-testid="schedule-timezone-hint"
              className="text-[10px] uppercase tracking-widest text-muted-foreground"
            >
              Times in workspace timezone · {tz}
            </div>
            {localPreview && (
              <div
                data-testid="schedule-local-preview"
                className="rounded-md bg-secondary/40 p-2 text-xs"
              >
                <CalendarClock className="mr-1 inline h-3.5 w-3.5 align-text-top text-primary" />
                {localPreview}
                {inPast && (
                  <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    Pick a time in the future
                  </span>
                )}
              </div>
            )}

            <div>
              <label
                htmlFor="schedule-notes"
                className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-muted-foreground"
              >
                Anything special? (optional)
              </label>
              <Textarea
                id="schedule-notes"
                data-testid="schedule-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. tie into the newsletter drop"
                rows={2}
              />
            </div>
          </div>
        )}

        {confirming && utcISO && (
          <div
            data-testid="schedule-confirm"
            className="rounded-md border border-border/60 bg-secondary/25 p-3 text-sm"
          >
            <div className="font-medium">Schedule this asset?</div>
            <div className="mt-2 space-y-1 text-xs">
              <div>
                <span className="text-muted-foreground">Where </span>
                <span className="font-medium">
                  {channels.map((c) => DESTINATIONS.find((d) => d.key === c)?.label).join(" · ")}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">When </span>
                <span className="font-medium">{localPreview}</span>
              </div>
              <div className="text-muted-foreground">
                Nothing will publish now. Orkexra will safely hold this until its scheduled time.
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!confirming && (
            <>
              <Button
                variant="outline"
                data-testid="schedule-cancel"
                onClick={() => onOpenChange(false)}
                disabled={submit.isPending}
              >
                Cancel
              </Button>
              <Button
                data-testid="schedule-continue"
                onClick={() => setConfirming(true)}
                disabled={!canSubmit}
              >
                Continue
                <ChevronDown className="ml-1 h-3.5 w-3.5 -rotate-90" />
              </Button>
            </>
          )}
          {confirming && (
            <>
              <Button
                variant="outline"
                data-testid="schedule-back"
                onClick={() => setConfirming(false)}
                disabled={submit.isPending}
              >
                Back
              </Button>
              <Button
                data-testid="schedule-confirm-submit"
                onClick={() => submit.mutate()}
                disabled={!canSubmit || submit.isPending}
              >
                {submit.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Scheduling…
                  </>
                ) : (
                  "Schedule"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Patch 35.3c · lightweight agenda block for the pack view. */
export function ScheduleAgenda({
  workspaceId, packId,
}: { workspaceId: string; packId: string }): JSX.Element | null {
  const q = useQuery({
    queryKey: ["calendar", workspaceId],
    queryFn: () => getCalendar(workspaceId),
    enabled: !!workspaceId && !!packId,
    staleTime: 30_000,
  });
  const items = (q.data || []).filter(
    (e) => e.campaign_pack_id === packId
      && e.publishing_status !== "cancelled"
      && e.publishing_status !== "published",
  ).slice(0, 5);
  if (items.length === 0) return null;
  return (
    <section
      data-testid="schedule-agenda"
      className="mt-2 rounded-md border border-border/60 bg-background/40 p-2 text-xs"
    >
      <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        Upcoming schedule
      </div>
      <ul className="space-y-0.5">
        {items.map((e) => (
          <li
            key={e.id}
            data-testid={`schedule-agenda-item-${e.id}`}
            className="flex items-center justify-between gap-2"
          >
            <span className="truncate font-medium">{e.asset_title}</span>
            <span className="whitespace-nowrap text-muted-foreground">
              {e.channel_label} · {e.local_preview_at}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
